import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
  LanguageSelector,
} from "@tellescope/ui";
import { Eye, EyeOff, ArrowLeft, Check, X, CheckCircle } from "lucide-react";
import {
  useEnduserRequestPasswordReset,
  useEnduserResetPassword,
  useEnduserIsLoading,
  useEnduserLoginFlowError,
  useEnduserClearLoginFlow,
} from "../store/hooks";
import { i18n } from "@tellescope/i18n";

interface PasswordRequirement {
  key: string;
  label: string;
  validate: (password: string) => boolean;
}

const getPasswordRequirements = (): PasswordRequirement[] => [
  {
    key: "length",
    label: i18n.t("Password must be at least 8 characters", { ns: "auth" }),
    validate: (p) => p.length >= 8,
  },
  {
    key: "uppercase",
    label: i18n.t("Password must contain an uppercase letter", { ns: "auth" }),
    validate: (p) => /[A-Z]/.test(p),
  },
  {
    key: "lowercase",
    label: i18n.t("Password must contain a lowercase letter", { ns: "auth" }),
    validate: (p) => /[a-z]/.test(p),
  },
  {
    key: "numberOrSpecial",
    label: i18n.t("Password must contain a number or special character", { ns: "auth" }),
    validate: (p) => /[0-9@#!$%^&*()]/.test(p),
  },
];

type ResetMode = "request" | "reset";

export const EnduserResetPasswordPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("resetToken");

  // Determine mode based on token presence
  const mode: ResetMode = token ? "reset" : "request";

  // Store hooks
  const requestPasswordReset = useEnduserRequestPasswordReset();
  const resetPassword = useEnduserResetPassword();
  const isLoading = useEnduserIsLoading();
  const loginFlowError = useEnduserLoginFlowError();
  const clearLoginFlow = useEnduserClearLoginFlow();

  // Local state - Request mode
  const [email, setEmail] = useState("");
  const [requestSent, setRequestSent] = useState(false);

  // Local state - Reset mode
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearLoginFlow();
    };
  }, [clearLoginFlow]);

  // Password validation
  const passwordValidation = useMemo(() => {
    return getPasswordRequirements().map((req) => ({
      ...req,
      isValid: req.validate(password),
    }));
  }, [password]);

  const isPasswordValid = useMemo(() => {
    return passwordValidation.every((req) => req.isValid);
  }, [passwordValidation]);

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleRequestReset = useCallback(async () => {
    if (!email.includes("@")) return;

    const success = await requestPasswordReset(email);
    if (success) {
      setRequestSent(true);
    }
  }, [email, requestPasswordReset]);

  const handleResetPassword = useCallback(async () => {
    if (!token || !isPasswordValid || !passwordsMatch) return;

    const success = await resetPassword(token, password);
    if (success) {
      setResetSuccess(true);
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 3000);
    }
  }, [token, isPasswordValid, passwordsMatch, password, resetPassword, navigate]);

  const handleBack = useCallback(() => {
    navigate("/login");
  }, [navigate]);

  // Request Mode - Email sent success
  if (mode === "request" && requestSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold">{t("Check Your Email")}</CardTitle>
            <CardDescription>{t("Check your email for reset instructions")}</CardDescription>
          </CardHeader>

          <CardContent className="text-center text-muted-foreground">
            <p>{t("If an account exists for {{email}}, you will receive a password reset link.", { email })}</p>
          </CardContent>

          <CardFooter className="flex flex-col space-y-3">
            <Button onClick={handleBack} className="w-full">
              {t("Back to Sign In")}
            </Button>

            <div className="w-full flex justify-center pt-2">
              <LanguageSelector />
            </div>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Reset Mode - Success
  if (mode === "reset" && resetSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold">{t("Password Reset")}</CardTitle>
            <CardDescription>{t("Your password has been reset successfully")}</CardDescription>
          </CardHeader>

          <CardContent className="text-center text-muted-foreground">
            <p>{t("Redirecting to sign in...")}</p>
          </CardContent>

          <CardFooter className="flex justify-center">
            <LanguageSelector />
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">{t("Reset Password")}</CardTitle>
          <CardDescription>
            {mode === "request" ? t("Enter your email to receive a reset link") : t("Enter your new password")}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Request Mode */}
          {mode === "request" && (
            <div className="space-y-2">
              <Label htmlFor="email">{t("Email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                autoComplete="email"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && email.includes("@")) {
                    handleRequestReset();
                  }
                }}
              />
            </div>
          )}

          {/* Reset Mode */}
          {mode === "reset" && (
            <>
              {/* New Password field */}
              <div className="space-y-2">
                <Label htmlFor="password">{t("New Password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    autoFocus
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Password requirements */}
              {password.length > 0 && (
                <div className="space-y-1">
                  {passwordValidation.map((req) => (
                    <div
                      key={req.key}
                      className={`flex items-center gap-2 text-xs ${
                        req.isValid ? "text-green-600" : "text-muted-foreground"
                      }`}
                    >
                      {req.isValid ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                      <span>{req.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Confirm Password field */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("Confirm Password")}</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-xs text-destructive">{t("Passwords do not match")}</p>
                )}
              </div>
            </>
          )}

          {loginFlowError && (
            <Alert variant="destructive">
              <AlertDescription>{loginFlowError}</AlertDescription>
            </Alert>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-3">
          {mode === "request" && (
            <>
              <Button onClick={handleRequestReset} disabled={!email.includes("@") || isLoading} className="w-full">
                {isLoading ? t("Sending...") : t("Send Reset Link")}
              </Button>
              <Button variant="ghost" onClick={handleBack} className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("Back to Sign In")}
              </Button>
            </>
          )}

          {mode === "reset" && (
            <>
              <Button
                onClick={handleResetPassword}
                disabled={!isPasswordValid || !passwordsMatch || isLoading}
                className="w-full"
              >
                {isLoading ? t("Resetting...") : t("Reset Password")}
              </Button>
              <Link to="/login" className="w-full">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t("Back to Sign In")}
                </Button>
              </Link>
            </>
          )}

          <div className="w-full flex justify-center pt-2">
            <LanguageSelector />
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};
